import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { AccordianModel } from 'src/app/models/accordian';
import { LoginType } from 'src/app/constants/login-type';
import { SessionService } from 'src/app/services/storage/session.service';

@Component({
    selector: 'mwp-login-faq',
    templateUrl: './faq.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginFAQComponent implements OnInit {
    @Input() public LoginType: string;

    public faqList: AccordianModel[] = new Array<AccordianModel>();

    constructor(
        private sessionService: SessionService,
    ) {
    }

    ngOnInit(): void {
        switch (this.LoginType) {
            case LoginType.Counselor:
                this.buildSupportedBrowser(true);
                this.buildCounselorMessage();
                this.buildLoginProcess();
                this.buildForgotPassword();
                this.build30LockedAccount();
                this.buildIEIncompatible();
                break;

            case LoginType.Member:
                this.buildSupportedBrowser(true);
                this.buildMemberMessage();
                this.buildRegistrationProcess();
                this.buildLoginProcess();
                this.buildCantFindProfile();
                this.buildForgotPassword();
                this.buildCantRembmerEmail();
                this.buildIEIncompatible();
                break;

            case LoginType.FirstResponder:
                this.buildSupportedBrowser(true);
                this.buildRegistrationProcess();
                this.buildLoginProcess();
                this.buildRegisterAccount();
                this.buildCantFindProfile();
                this.buildForgotPassword();
                this.buildForgotEmail();
                this.buildIEIncompatible();
                this.buildEmailProviders(false);
                break;

            case LoginType.Registration:
                this.buildEmailProviders(true);
                this.buildSupportedBrowser(false);
                this.buildRegisterAccount();
                this.buildCantFindProfile();
                break;

            case LoginType.NewPassword:
                this.buildNewPasswordTitle();
                this.buildSupportedBrowser(false);
                this.buildEmailProviders(false);
                break;

            case LoginType.ResetPassword:
                this.buildResetPassword(true);
                this.buildSupportedBrowser(false);
                this.buildEmailProviders(false);
                break;
        }
    }

    public closeFAQs() {
        this.sessionService.session.isFaqActive = false;
        this.sessionService.setSession();
    }

    private sanitizeJsonKey(jsonKey: string, isTitle: boolean): string {
        const jsonKeyTitlePrefix: string = 'faqTitle';
        const jsonKeyBodyPrefix: string = 'faqBody';
        if (isTitle) {
            return `${jsonKeyTitlePrefix}${jsonKey.replace(/<[^>]+>/g, '').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '').substring(0, 30)}`;
        }
        else {
            return `${jsonKeyBodyPrefix}${jsonKey.replace(/<[^>]+>/g, '').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '').substring(0, 30)}`;
        }
    }

    private buildSupportedBrowser(isExpanded: boolean): void {
        let title: string = '';
        let jsonKeyTitle: string;
        switch (this.LoginType) {
            case LoginType.FirstResponder:
                title = `Welcome to the Employee section of MyWorkplace!`;
                jsonKeyTitle = this.sanitizeJsonKey(title, true);
                break;

            case LoginType.Registration:
            case LoginType.ResetPassword:
            case LoginType.NewPassword:
            case LoginType.Counselor:
            case LoginType.Member:
                title = `What browsers does MyWorkplace.net support?`;
                jsonKeyTitle = this.sanitizeJsonKey(title, true);
                break;
        }

        const body = `<div class="container-supported-browsers mb-3">
                <div class="d-flex justify-content-center text-subtitle">
                   Supported browsers for MyWorkplace.net are listed below. You may obtain the latest version of any of these browsers by clicking its icon.
                </div>
                <div class="d-flex justify-content-center mb-3">
                    <div class="container-email-services modal-box w-100">
                        <div class="d-inline-flex justify-content-center browser-panel mx-4">
                            <a href="https://www.google.com/chrome/browser/desktop/" class="browser-icon gmail me-4" target="_blank">
                                <div class="d-flex justify-content-center mb-1">
                                    <img src="assets/images/Chrome_Logo.svg" alt="Google Chrome Logo" />
                                </div>
                                <div>Google Chrome</div>
                            </a>
                            <a href="https://www.mozilla.org/en-US/firefox/new/" class="browser-icon me-4" target="_blank">
                                <div class="d-flex justify-content-center mb-1 mt-1">
                                    <img src="assets/images/Firefox_Logo.svg" alt="Mozilla FireFox Logo" />
                                </div>
                                <div>Mozilla Firefox</div>
                            </a>
                            <a href="https://support.apple.com/downloads/safari" class="browser-icon me-4" target="_blank">
                                <div class="d-flex justify-content-center mb-1 mt-1">
                                    <img src="assets/images/Safari_Logo.svg" alt="Apple Safari Logo" />
                                </div>
                                <div>Apple Safari</div>
                            </a>
                            <a href="https://www.microsoft.com/en-us/windows/microsoft-edge" class="browser-icon" target="_blank">
                                <div class="d-flex justify-content-center mb-1 mt-1">
                                    <img src="assets/images/Edge_Logo.svg" alt="Microsoft Edge Logo" />
                                </div>
                                <div>Microsoft Edge</div>
                            </a>
                        </div>
                    </div>
                </div>
                <div class="container-supported-browsers-note">
                    Please note: Internet Explorer is NOT a supported browser.
                </div>
            </div>`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildEmailProviders(isExpanded: boolean): void {
        const title = `Free email provider links`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `<div class="container-email-services w-100 mb-2">
                        <div class="d-flex justify-content-between browser-panel mx-4">
                            <a href="https://accounts.google.com/SignUp" class="browser-icon gmail" target="_blank">
                                <div class="d-flex justify-content-center mb-1">
                                    <img src="assets/images/Gmail_Logo.svg" alt="Gmail Logo" />
                                </div>
                                <div>Gmail</div>
                            </a>
                            <a href="https://login.yahoo.com/account/create" class="browser-icon yahoo" target="_blank" >
                                <div class="d-flex justify-content-center mb-1 mt-1">
                                    <img src="assets/images/Yahoo_Mail_Logo.svg" alt="Yahoo! Mail Logo" />
                                </div>
                                <div>Yahoo! Email</div>
                            </a>
                            <a href="https://signup.live.com" class="browser-icon outlook" target="_blank" >
                                <div class="d-flex justify-content-center mb-1 mt-1" >
                                    <img src="assets/images/Outlook_Logo.webp" alt="Outlook Logo" />
                                </div>
                                <div>Outlook</div>
                            </a>
                        </div>
                    </div>`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildCounselorMessage(): void {
        const title = `Welcome Benefits Counselors!`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `If you are a field or call center-based counselor, you can start assisting employees with their benefits
                enrollments here. Logging in will direct you to the counselor management portal where you can access
                employee profiles, access call history, schedule follow ups, access reports and resources, and, for
                supervisors, manage your counselors within various enrollments.`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildMemberMessage(): void {
        const title = `Welcome to the Employee section of MyWorkplace!`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `Here you can log into your portal to review work related items such as employee benefits, payroll, scheduling, etc. You can
                        perform functions such as requesting qualifying life event (QLE) changes to your benefits, completing benefit enrollment
                        sessions, making HSA contribution adjustments, submitting address and contact information changes and much more.
                    <div class="gold-box mt-2">
                        <div>
                            Please note that not all employer groups use all options available in MyWorkplace. The portal will configure and display those services enabled by your employer.
                        </div>
                    </div>`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildRegistrationProcess(): void {
        const title = `Registration Process - First Time Users`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);

        let body: string;
        switch (this.LoginType) {
            case LoginType.FirstResponder:
                body = `Here you can log into your portal to review work related items such as employee benefits, payroll, scheduling, etc. You can
								perform functions such as requesting qualifying life event (QLE) changes to your benefits, completing benefit enrollment
								sessions, making HSA contribution adjustments, submitting address and contact information changes and much more.
							<div class="gold-box mt-2">
								<div class="m-2 ps-2">
									Please note that not all employer groups use all options available in MyWorkplace. The portal will configure
										and display those services enabled by your employer.
								</div>
							</div>`;
                break;

            case LoginType.Member:
                body = `Use the Reset Password option. Note, you will need access to the email account you used
								to register in order to complete the password reset process.`;
                break;
        }
        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildLoginProcess(): void {
        const title = `Logging In`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        let body = `<div class="mb-2">
                        <div class="gold-header mb-3">
                            To login to your portal
                        </div>
                        
                        <ol class="gold-list mb-3">
                            <li>
                                Enter your user name and password
                            </li>
                            <li>
                                Enter your location (Work Site or Call Center)
                            </li>
                            <ul class="gold-sub-list">
                                <li>
                                    If you select work site, you will be prompted for the city and state of your location
                                </li>
                            </ul>
                            <li>
                                Complete the “I'm Not a Robot” captcha
                            </li>
                            <li>
                                Click the Submit button
                            </li>
                        </ol>
                    </div>
                    <div>
                        If you have issues logging in, please review the FAQ below for assistance.
                    </div>`;

        switch (this.LoginType) {
            case LoginType.FirstResponder:
                body = `${body} <div class="mt-3">
                            If you are a new user of MyWorkplace, you must complete the Registration process before you can begin using the portal. To
                            register, click the Register Here button above and follow the instructions on the subsequent page(s). Note, you need to
                            provide an email address as part of the registration process. If you don't have an email address, the registration page
                            provides links to several free email account providers you may wish to use to setup an email account.
                        </div>`;
                break;
        }

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildCantFindProfile(): void {
        const title = `I'm trying to register with MyWorkplace but the system says it can't find my profile:`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `There are several possibilities that may be occurring:
                    <ol class="gold-list">
                        <li>Have you recently moved? If so, we may not have your most current zip code on file.
                            Try the registration process using your previous zip code.
                        </li>
                        <li>Double check the last four of SSN, date of birth, and home zip code entered for
                            accuracy. If these are correct, it may be that we have an incorrect value for one of
                            these items in the database and are unable to match your profile data to the entries
                            you have made. Please contact your human resources department to have your profile
                            information updated and, once done, proceed with the registration process.
                        </li>
                        <li>We may not have added your profile to MWP yet. Please check with your human
                            resources department to verify if your profile has been setup in the system. Once it
                            has, proceed with the registration process.
                        </li>
                    </ol>`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildForgotPassword(): void {
        let title: string;
        let body: string;
        let jsonKeyTitle: string;
        switch (this.LoginType) {
            case LoginType.Counselor:
                title = `I'm trying to login but have forgotten my password:`;
                jsonKeyTitle = this.sanitizeJsonKey(title, true);
                body = `Contact the supervising counselor for the group you are working on and ask them to reset
                        your password.
                        You will be sent an email containing a link to initiate the password change process. Please
                        note that the
                        email sent with the link is only valid for 24 hours.`;
                break;

            case LoginType.FirstResponder:
            case LoginType.Member:
                title = `I'm trying to login as a Returning User but have forgotten my password:`;
                jsonKeyTitle = this.sanitizeJsonKey(title, true);
                body = `Use the Reset Password option. Note, you will need access to the email account you used
								to register in order to complete the password reset process.`;
                break;
        }

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildCantRembmerEmail(): void {
        const title = `I completed the Registration process in the past but can't remember the email address I used:`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `Contact your human resource department and ask them to clear your existing MWP portal
								registration credentials. Once that has been done, complete the Registration process again.`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private build30LockedAccount(): void {
        const title = `I haven't logged into MWP in the last 30 days and my account is locked:`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `Contact the supervising counselor for the group you are working on and ask them to reset
            your account. You will be sent an email containing a link to setup a new password which will then
            reactivate your counselor login credentials. Please note that the email sent with the link is only valid for 
            24 hours.`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildIEIncompatible(): void {
        const title = `Why can't I use Internet Explorer (IE)?`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `Internet Explorer is an older browser platform which doesn't support the requirements of more modern
            programming environments used to create sites such as MyWorkplace.net. Support for IE is being discontinued
            by Microsoft which replaced IE with Microsoft's Edge browser with the release of Windows 10.
            You must use one of the supported browsers illustrated above for MyWorkplace to function properly. The
            icons for the various browsers have associated hyperlinks to their manufactures' sites for downloading the
            installers for the most recent versions available.`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildForgotEmail(): void {
        const title = `I completed the Registration process in the past but can't remember the email address I used:`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `Contact your human resource department and ask them to clear your existing MWP portal registration credentials. Once that has been done, complete the Registration process again.`;

        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildNewPasswordTitle(): void {
        const title = `Create Your New Password`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `Please create your password (case sensitive), and you will be logged in to MyWorkplace.`;
        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildResetPassword(isExpanded: boolean): void {
        const title = `Resetting Your Password`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `<div class="gold-header">
                            To reset your password
                        </div>
                        <div>
                            <ol class="gold-list">
                                <li>Enter your registered email address</li>
                                <li>Complete the “I'm Not a Robot” captcha</li>
                                <li>Click the Submit button</li>
                                <li>Check your email. A confirmation email will be sent to your registered email address with a password reset link.</li>
                                <li>Click the link in the email to reset your password.</li>
                            </ol>
                        </div>`;
        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private buildRegisterAccount(): void {
        const title = `Registration for New Users!`;
        const jsonKeyTitle = this.sanitizeJsonKey(title, true);
        const body = `<div class="gold-header">
                            To register for your account:
                        </div>
                        <div class="list-gold-numeral">
                            <ol>
                                <li>Enter a valid email address</li>
                                <ul class="gold-sub-list">
                                    <li>Please use an email address that you currently have access to</li>
                                    <li>If you don't have an email address, this registration page provides links to several free email account providers you
                                        may wish to use to setup an email account</li>
                                </ul>
                                <li>Confirm your email address</li>
                                <li>Input your date of birth</li>
                                <li>Input the last 4 digits of your social security number</li>
                                <li>Input your home address zip code</li>
                                <li>Select how you would like to verify your account</li>
                                <ul class="gold-sub-list">
                                    <li>If you select Email Address, a link will be sent your email account. Please login to your email account and click
                                        the link provided to create your password</li>
                                    <li>If you select Text Message, a text message will be sent to the phone number you provided with a verification
                                        code. You will then be prompted to enter the code. It will expire 5 minutes after being sent</li>
                                    <li>If you select Phone Call, An automated system will call the phone number you provided and say the verification
                                        code. You will then be prompted to enter the code. It will expire 5 minutes after the call hangs up</li>
                                </ul>
                                <li>Complete the “I'm Not a Robot” captcha</li>
                                <li>Click the Submit button</li>
                            </ol>
                        </div>
                        <div class="mt-2">
                            If you have issues logging in, please review the FAQ below for assistance.
                        </div>`;
        const jsonKeyBody = this.sanitizeJsonKey(title, false);
        this.constructFaqList(false, title, body, jsonKeyTitle, jsonKeyBody);
    }

    private constructFaqList(isExpanded: boolean, title: string, body: string, jsonKeyTitle: string, jsonKeyBody: string): void {
        const l_Accordian: AccordianModel = {
            isExpanded: isExpanded,
            title: title,
            body: body,
            jsonKeyTitle: jsonKeyTitle,
            jsonKeyBody: jsonKeyBody
        };
        this.faqList.push(l_Accordian);
    }
}